import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  canPromptInstall,
  promptInstall,
  subscribeInstallPrompt,
  isIOSDevice as isIOS,
  isRunningInstalled as isStandalone,
} from '../utils/installApp';

export default function InstallPrompt() {
  // Whether the browser has offered installation (captured in utils/installApp).
  const [deferredPrompt, setDeferredPrompt] = useState(canPromptInstall);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  // Guards against double-reloading: controllerchange can fire more than once
  // when multiple tabs are open or when the SW activates concurrently with a
  // manual reload.
  const refreshingRef = useRef(false);

  // Listen for beforeinstallprompt (Chrome/Edge/Samsung)
  useEffect(() => {
    if (isStandalone() || localStorage.getItem('install_prompt_dismissed')) {
      return;
    }

    const sync = () => {
      const available = canPromptInstall();
      setDeferredPrompt(available);
      if (available) setShowBanner(true);
    };

    sync();
    return subscribeInstallPrompt(sync);
  }, []);

  // iOS detection
  useEffect(() => {
    if (isStandalone() || localStorage.getItem('install_prompt_dismissed')) {
      return;
    }
    if (isIOS() && !deferredPrompt) {
      setShowIOSInstructions(true);
      setShowBanner(true);
    }
  }, [deferredPrompt]);

  // Listen for service worker update
  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  // When the new SW takes control (after skipWaiting + activate), reload so
  // the page is served by it. Reloading earlier — right after postMessage —
  // re-runs the OLD worker, leaving the new one stuck in "waiting" and the
  // banner re-appears on next load. The controllerchange event is the only
  // reliable handoff point.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onControllerChange = () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      console.log('PWA: User accepted install prompt');
    }
    setShowBanner(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem('install_prompt_dismissed', 'true');
    setShowBanner(false);
    setShowIOSInstructions(false);
  }, []);

  const handleUpdate = useCallback(() => {
    if (updating) return;
    setUpdating(true);

    const registration = window.__SW_REGISTRATION;

    // No registration at all — the banner is stale. Hide it and reload to
    // pick up whatever the active SW has cached. We also clear the global
    // so a future onUpdate can re-arm cleanly.
    if (!registration) {
      window.__SW_REGISTRATION = null;
      setUpdateAvailable(false);
      window.location.reload();
      return;
    }

    if (registration.waiting) {
      // Ask the waiting worker to skip. controllerchange listener (above)
      // performs the reload once the new worker takes control.
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Safety net: if controllerchange never fires (e.g., the SW errors
      // during activation), reload after a short timeout so the user isn't
      // stuck with a non-responsive button.
      setTimeout(() => {
        if (!refreshingRef.current) {
          refreshingRef.current = true;
          window.location.reload();
        }
      }, 3000);
      return;
    }

    // No waiting worker — either the SW already activated in the background
    // or the registration lost its waiting reference. Hide the stale banner
    // and reload so the user sees the active version.
    setUpdateAvailable(false);
    window.__SW_REGISTRATION = null;
    window.location.reload();
  }, [updating]);

  // Update banner
  if (updateAvailable) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between px-4 py-3 shadow-lg"
        style={{ backgroundColor: '#1e40af', color: '#fff', paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <span className="text-sm font-medium">
          {updating ? 'Updating…' : 'New version available!'}
        </span>
        <button
          onClick={handleUpdate}
          disabled={updating}
          className="ml-3 text-sm font-semibold px-3 py-1 rounded-lg disabled:opacity-60"
          style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
        >
          {updating ? 'Please wait…' : 'Update now'}
        </button>
      </div>
    );
  }

  // Install banner
  if (!showBanner) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between px-4 py-3 shadow-lg"
      style={{ backgroundColor: '#8B0000', color: '#fff', paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
    >
      <div className="flex-1 min-w-0 mr-3">
        {showIOSInstructions ? (
          <span className="text-sm">
            To install: tap{' '}
            <svg className="inline w-4 h-4 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" /></svg>
            {' '}then <strong>"Add to Home Screen"</strong>
          </span>
        ) : (
          <span className="text-sm font-medium">Install Komis POS for a better experience</span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!showIOSInstructions && (
          <button
            onClick={handleInstall}
            className="text-sm font-semibold px-3 py-1 rounded-lg"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="text-sm px-2 py-1 rounded-lg opacity-80 hover:opacity-100"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
